import React, { useState, useEffect } from "react";
import { Award, TrendingUp, DollarSign, Search, Headphones, Zap, ChevronLeft, ChevronRight } from "lucide-react";
import { whyChooseUsData } from "../data/mock";

const iconMap = {
  Award: Award,
  TrendingUp: TrendingUp,
  DollarSign: DollarSign,
  Search: Search,
  Headphones: Headphones,
  Zap: Zap
};

const WhyChooseSection = ({ onOpenForm }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener("resize", checkMobile);
    
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % whyChooseUsData.reasons.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + whyChooseUsData.reasons.length) % whyChooseUsData.reasons.length);
  };

  return (
    <section id="why-choose" className="section-padding bg-white">
      <div className="container-custom px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-[#d4af37] font-semibold text-sm uppercase tracking-wider">
            Why Choose Us
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mt-3 mb-6">
            {whyChooseUsData.title}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            {whyChooseUsData.subtitle}
          </p>
        </div>

        {/* Desktop Grid */}
        {!isMobile && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyChooseUsData.reasons.map((reason, index) => {
              const IconComponent = iconMap[reason.icon];
              return (
                <div
                  key={index}
                  className="group bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 card-hover"
                >
                  {/* Icon */}
                  <div className="w-16 h-16 bg-gradient-primary rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-4 group-hover:text-[#1e3a8a] transition-colors duration-300">
                    {reason.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {reason.description}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {/* Mobile Carousel */}
        {isMobile && (
          <div className="relative">
            <div className="overflow-hidden">
              <div
                className="flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentSlide * 100}%)` }}
              >
                {whyChooseUsData.reasons.map((reason, index) => {
                  const IconComponent = iconMap[reason.icon];
                  return (
                    <div key={index} className="w-full flex-shrink-0 px-4">
                      <div className="bg-gradient-to-br from-white to-gray-50 p-8 rounded-2xl shadow-lg border border-gray-100">
                        <div className="w-16 h-16 bg-gradient-primary rounded-xl flex items-center justify-center mb-6 shadow-lg">
                          <IconComponent className="w-8 h-8 text-white" />
                        </div>

                        <h3 className="text-xl font-bold text-gray-900 mb-4">
                          {reason.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {reason.description}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Navigation */}
            <button
              onClick={prevSlide}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white transition-all duration-300"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-white rounded-full shadow-lg flex items-center justify-center text-[#1e3a8a] hover:bg-[#1e3a8a] hover:text-white transition-all duration-300"
            >
              <ChevronRight className="w-6 h-6" />
            </button>

            <div className="flex justify-center mt-8 space-x-2">
              {whyChooseUsData.reasons.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-300 ${
                    currentSlide === index ? "bg-[#1e3a8a] w-8" : "bg-gray-300"
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-[#1e3a8a]/5 via-[#d4af37]/5 to-[#1e3a8a]/5 p-8 rounded-2xl border border-[#d4af37]/20">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Experience the Trademark Brand Logo Difference
            </h3>
            <p className="text-gray-700 max-w-2xl mx-auto mb-6 leading-relaxed">
              Join thousands of satisfied clients who have trusted us to protect their most valuable business assets. 
              Our proven track record, expert team, and commitment to excellence make us the premier choice for 
              trademark registration and intellectual property protection services.
            </p>
            <button onClick={onOpenForm} className="btn-primary text-lg">
              Start Your Trademark Registration
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
